/**
 * ParseError — structured error class for flexible-xml-parser.
 *
 * All errors thrown by the parser are instances of ParseError so callers can
 * distinguish library errors from generic runtime errors and reliably inspect
 * position information.
 *
 * @property {string}          code     - Machine-readable error code (e.g. 'UNEXPECTED_CLOSE_TAG')
 * @property {number|undefined} index   - 0-based character offset from document start (when available)
 */
export class ParseError extends Error {
  /**
   * @param {string} message       - Human-readable error message
   * @param {string} code          - Machine-readable error code
   * @param {object} [position]    - Optional position info
   * @param {number} [position.index]
   */
  constructor(message, code, position = {}) {
    super(message);
    this.name = 'ParseError';
    this.code = code;

    this.index = position.index ?? undefined;
  }

  toString() {
    const pos = this._posStr();
    return pos ? `${this.name} [${this.code}] at ${pos}: ${this.message}` : `${this.name} [${this.code}]: ${this.message}`;
  }

  _posStr() {
    if (this.index !== undefined) {
      return `index ${this.index}`;
    }
    return null;
  }
}

// ─── Error codes ─────────────────────────────────────────────────────────────

export const ErrorCode = Object.freeze({
  // Input type errors
  INVALID_INPUT: 'INVALID_INPUT',
  INVALID_STREAM: 'INVALID_STREAM',

  // Streaming / feed API
  ALREADY_STREAMING: 'ALREADY_STREAMING',
  NOT_STREAMING: 'NOT_STREAMING',
  DATA_MUST_BE_STRING: 'DATA_MUST_BE_STRING',

  // Tag structure
  UNEXPECTED_END: 'UNEXPECTED_END',
  UNEXPECTED_CLOSE_TAG: 'UNEXPECTED_CLOSE_TAG',
  MISMATCHED_CLOSE_TAG: 'MISMATCHED_CLOSE_TAG',
  UNEXPECTED_TRAILING_DATA: 'UNEXPECTED_TRAILING_DATA',
  INVALID_TAG: 'INVALID_TAG',
  UNCLOSED_QUOTE: 'UNCLOSED_QUOTE',
  INVALID_ATTRIBUTE_NAME: 'INVALID_ATTRIBUTE_NAME',

  // Namespace
  MULTIPLE_NAMESPACES: 'MULTIPLE_NAMESPACES',

  // Conformance (illegal chars, attribute value rules)
  ILLEGAL_CHARACTER: 'ILLEGAL_CHARACTER',
  DUPLICATE_ATTRIBUTE: 'DUPLICATE_ATTRIBUTE',
  UNQUOTED_ATTRIBUTE_VALUE: 'UNQUOTED_ATTRIBUTE_VALUE',
  BOOLEAN_ATTRIBUTE_REJECTED: 'BOOLEAN_ATTRIBUTE_REJECTED',

  // Security
  SECURITY_PROTOTYPE_POLLUTION: 'SECURITY_PROTOTYPE_POLLUTION',
  SECURITY_RESERVED_OPTION: 'SECURITY_RESERVED_OPTION',
  SECURITY_RESTRICTED_NAME: 'SECURITY_RESTRICTED_NAME',

  // Limits (DoS prevention)
  LIMIT_MAX_NESTED_TAGS: 'LIMIT_MAX_NESTED_TAGS',
  LIMIT_MAX_ATTRIBUTES: 'LIMIT_MAX_ATTRIBUTES',

  // Entity limits
  ENTITY_MAX_COUNT: 'ENTITY_MAX_COUNT',
  ENTITY_MAX_SIZE: 'ENTITY_MAX_SIZE',
  ENTITY_MAX_EXPANSIONS: 'ENTITY_MAX_EXPANSIONS',
  ENTITY_MAX_EXPANDED_LENGTH: 'ENTITY_MAX_EXPANDED_LENGTH',

  // Entity registration
  ENTITY_INVALID_KEY: 'ENTITY_INVALID_KEY',
  ENTITY_INVALID_VALUE: 'ENTITY_INVALID_VALUE',

  // Encoding
  UNSUPPORTED_ENCODING: 'UNSUPPORTED_ENCODING',
  INVALID_DECODER: 'INVALID_DECODER',
  ENCODING_MISMATCH: 'ENCODING_MISMATCH',
});

export default ParseError;
